import { Directive, HostBinding, Input } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { HelpText } from '../../model/help-text';
import { FormControlEx } from '../forms/form-control';
import { shouldShowAstralValidationError } from './astral-validation-state';

export function getAstralFieldHelpId(inputId: string): string {
  return `${inputId}-help`;
}

export function getAstralFieldErrorId(inputId: string): string {
  return `${inputId}-error`;
}

export function getAstralFieldLabelId(inputId: string): string {
  return `${inputId}-label`;
}

@Directive({
  selector: '[astralFieldAccessibility]',
  standalone: true,
})
export class AstralFieldAccessibilityDirective {
  @Input() astralFieldControl: AbstractControl | null | undefined;
  @Input() astralFieldHelpText: string | HelpText | null | undefined;
  @Input() astralFieldInputId: string;
  @Input() astralFieldInvalid: boolean | null | undefined;
  @Input() astralFieldRequired: boolean | null | undefined;

  private get controlEx(): FormControlEx | null {
    return this.astralFieldControl as FormControlEx | null;
  }

  private get hasHelpText(): boolean {
    const helpText = this.astralFieldHelpText ?? this.controlEx?.helpText;
    return typeof helpText === 'string' ? !!helpText : !!helpText?.text;
  }

  private get invalid(): boolean {
    return this.astralFieldInvalid ?? shouldShowAstralValidationError(this.controlEx);
  }

  @HostBinding('attr.aria-describedby')
  get describedBy(): string | null {
    if (!this.astralFieldInputId) {
      return null;
    }

    const ids: string[] = [];
    if (this.hasHelpText) {
      ids.push(getAstralFieldHelpId(this.astralFieldInputId));
    }
    if (this.invalid) {
      ids.push(getAstralFieldErrorId(this.astralFieldInputId));
    }
    return ids.length ? ids.join(' ') : null;
  }

  @HostBinding('attr.aria-errormessage')
  get errorMessageId(): string | null {
    return this.astralFieldInputId && this.invalid ? getAstralFieldErrorId(this.astralFieldInputId) : null;
  }

  @HostBinding('attr.aria-invalid')
  get ariaInvalid(): 'true' | null {
    return this.invalid ? 'true' : null;
  }

  @HostBinding('attr.aria-required')
  get ariaRequired(): 'true' | null {
    const required = this.astralFieldRequired ?? this.controlEx?.required ?? false;
    return required ? 'true' : null;
  }
}
